import React from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import {
  SettingsLayout,
  UserSettingsPage,
} from '@backstage/plugin-user-settings';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { Sev2ProjectComboboxFieldExtension } from './scaffolder/Sev2ProjectCombobox';
import { Sev2TagComboboxFieldExtension } from './scaffolder/Sev2TagCombobox/extensions';
import { Sev2UserComboboxFieldExtension } from './scaffolder/Sev2UserCombobox';
import { Sev2TaskComboboxFieldExtension } from './scaffolder/Sev2TaskCombobox';
import { apis } from './apis';
import { Root } from './components/Root';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';

import { AlertDisplay } from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import CssBaseline from '@mui/material/CssBaseline';

import {
  configApiRef,
  googleAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';

import { Sev2BoardPage, FeedbackPage } from '@internal/backstage-plugin-sev2';
import {
  NotificationsPage,
  UserNotificationSettingsCard,
} from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { UptimeMonitorPage } from '@internal/backstage-plugin-uptime-monitor';

/** Custom Theme */
import {
  UnifiedThemeProvider,
  // themes
} from '@backstage/theme';
// import { polarisTheme } from './theme/polaris';
// import { CssBaseline } from '@material-ui/core';
import StarBorder from '@material-ui/icons/StarBorder';
import { RelatedGroupPickerExtension } from './scaffolder/RelatedGroupPicker';
import { ProjectPickerExtension } from './scaffolder/ProjectPicker';

import { HomepageCompositionRoot, VisitListener } from '@backstage/plugin-home';
import { HomePage } from './components/Home/HomePage';
import { RichTextEditorExtension } from './scaffolder/RichTextEditor';
import { ComponentTypePickerFieldExtension } from './scaffolder/ComponentTypePicker';
import { SignedInFirebaseNotificationListener } from '@internal/backstage-plugin-notifier';
import { zitadelAuthApiRef } from './auth';
import { FigmaPage } from '@internal/backstage-plugin-figma';
import { UserEntityPickerFieldExtension } from './scaffolder/UserEntityPicker/extensions';
import { StoryPickerExtension } from './scaffolder/StoryPicker';
import { BillingCompaniesPickerExtension } from './scaffolder/BillingCompaniesPicker';
import { BillingProductsPickerExtension } from './scaffolder/BillingProductsPicker';
import { SystemComponentPickerExtension } from './scaffolder/SystemComponentPicker';
import { EntityKubernetesContent } from '@backstage/plugin-kubernetes';
import { PrometheusPage } from '@internal/backstage-plugin-prometheus';
import { konteksDarkTheme } from './theme/konteksDark';
import { konteksLightTheme } from './theme/konteksLight';
import { ExplorePage } from './components/explore/ExplorePage';
import { GatusMonitorPage } from '@internal/backstage-plugin-gatus-monitor';
import { UmamiAnalyticsPage } from '@internal/backstage-plugin-umami-analytics';
import { ManifestEditorPage } from '@internal/backstage-plugin-manifest-editor';
import { SimpleManifestEditorPage } from '@internal/backstage-plugin-simple-manifest-editor';
import { ZitadelAccessInterceptor } from './components/utils/ZitadelAccessInterceptor';
import { AllowedSystemsApiExplorerPage } from './components/api/AllowedSystemsApiExplorerPage';
import { AllowedSystemsTechDocsHome } from './components/techdocs/AllowedSystemsTechDocsHome';
import { AllowedSystemsCatalogIndexPage } from './components/catalog/AllowedSystemsCatalogIndexPage';
import { AccessTokenCard } from './components/settings/AccessTokenCard';
import { HandoverPage } from '@internal/backstage-plugin-handover';
import {
  AiAgentsPage,
  AiAgentDetailPage,
  ProviderAccountListPage,
  ProviderAccountDetailPage,
} from '@internal/backstage-plugin-ai-agents';
import { RbacPage } from '@backstage-community/plugin-rbac';
import { KonteksProgress } from './components/Progress';
import { CustomSignInPage } from './components/SignIn';
import { AutoTriggerOAuthDialog } from './components/Auth';
import {
  ProjOnboardPage,
  ProjOnboardProjectListPage,
  ProjOnboardProjectDetailPage,
} from '@internal/backstage-plugin-proj-onboard';
import {
  QuickStartPage,
  QuickStartGuard,
} from '@internal/backstage-plugin-quick-start';
import {
  BillingInvoicePage,
  BillingSpUsagePage,
  BillingOverviewPage,
  TermOfPaymentListPage,
  TermOfPaymentCreatePage,
  TermOfPaymentEditPage,
  InvoiceListPage,
  InvoiceCreatePage,
  InvoiceEditPage,
} from '@internal/backstage-plugin-billing';

/**
 * AppLayout component that handles routing between QuickStart and main app.
 * QuickStart page renders without sidebar, other pages render with Root layout.
 */
const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();

  // Quick start page renders without the sidebar/navigation
  // The page is rendered via FlatRoutes, but we skip the Root wrapper
  if (location.pathname === '/quick-start') {
    return <>{children}</>;
  }

  // All other pages render inside Root with sidebar, wrapped in QuickStartGuard
  return (
    <QuickStartGuard>
      <Root>{children}</Root>
    </QuickStartGuard>
  );
};

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    Progress: KonteksProgress,
    SignInPage: props => {
      const configApi = useApi(configApiRef);
      const isDev = configApi.getString('auth.environment') === 'development';
      return (
        <CustomSignInPage
          {...props}
          providers={[
            ...(isDev ? ['guest' as const] : []),
            {
              id: 'google-auth-provider',
              title: 'Google',
              message: 'Sign in using Google',
              apiRef: googleAuthApiRef,
            },
            {
              id: 'oauth2',
              title: 'External SSO',
              message: 'Sign in using External SSO Provider',
              apiRef: zitadelAuthApiRef,
            },
          ]}
          googleApiRef={googleAuthApiRef}
          externalSsoApiRef={zitadelAuthApiRef}
        />
      );
    },
  },
  themes: [
    {
      id: 'konteks-dark',
      title: 'Konteks Dark',
      variant: 'dark',
      icon: <StarBorder />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={konteksDarkTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    {
      id: 'konteks-light',
      title: 'Konteks Light',
      variant: 'light',
      icon: <StarBorder />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={konteksLightTheme}>
          <CssBaseline />
          {children}
        </UnifiedThemeProvider>
      ),
    },
    // {
    //   id: 'light',
    //   title: 'Light',
    //   variant: 'light',
    //   Provider: ({ children }) => (
    //     <UnifiedThemeProvider theme={themes.light} children={children} />
    //   ),
    // },
    // {
    //   id: 'dark',
    //   title: 'Dark',
    //   variant: 'dark',
    //   Provider: ({ children }) => (
    //     <UnifiedThemeProvider theme={themes.dark} children={children} />
    //   ),
    // },
    // {
    //   id: 'polaris',
    //   title: 'Polaris',
    //   variant: 'light',
    //   icon: <StarBorder />,
    //   Provider: ({ children }) => (
    //     <UnifiedThemeProvider theme={polarisTheme} noCssBaseline>
    //       <CssBaseline />
    //       {children}
    //     </UnifiedThemeProvider>
    //   ),
    // },
  ],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="home" />} />
    <Route path="/quick-start" element={<QuickStartPage />} />
    <Route path="/home" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route path="/catalog" element={<CatalogIndexPage />}>
      <AllowedSystemsCatalogIndexPage initialKind="system" />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <AllowedSystemsTechDocsHome />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <Sev2TagComboboxFieldExtension />
        <Sev2ProjectComboboxFieldExtension />
        <Sev2UserComboboxFieldExtension />
        <Sev2TaskComboboxFieldExtension />
        <ComponentTypePickerFieldExtension />
        <RelatedGroupPickerExtension />
        <ProjectPickerExtension />
        <RichTextEditorExtension />
        <UserEntityPickerFieldExtension />
        <StoryPickerExtension />
        <BillingCompaniesPickerExtension />
        <BillingProductsPickerExtension />
        <SystemComponentPickerExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<AllowedSystemsApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />}>
      <SettingsLayout.Route path="/notifications" title="Notifications">
        <UserNotificationSettingsCard
          originNames={{
            'plugin:notifier': 'Ticket Progress',
            'plugin:uptime-monitor': 'Uptime Monitoring',
          }}
        />
      </SettingsLayout.Route>
      <SettingsLayout.Route path="/access-token" title="Access Token">
        <AccessTokenCard />
      </SettingsLayout.Route>
    </Route>
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/sev-2-ticket" element={<Sev2BoardPage />} />
    <Route path="/feedback" element={<FeedbackPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/uptime" element={<UptimeMonitorPage />} />
    <Route path="/figma" element={<FigmaPage />} />
    <Route path="/kubernetes" element={<EntityKubernetesContent />} />
    <Route path="/prometheus" element={<PrometheusPage />} />
    <Route path="/explore" element={<ExplorePage />} />
    <Route path="/gatus-monitor" element={<GatusMonitorPage />} />
    <Route path="/umami-analytics" element={<UmamiAnalyticsPage />} />
    <Route path="/manifest-editor" element={<ManifestEditorPage />} />
    <Route
      path="/simple-manifest-editor"
      element={<SimpleManifestEditorPage />}
    />
    <Route path="/handover" element={<HandoverPage />} />
    <Route path="/projects" element={<ProjOnboardProjectListPage />} />
    <Route path="/projects/onboarding" element={<ProjOnboardPage />} />
    <Route path="/projects/:id/*" element={<ProjOnboardProjectDetailPage />} />
    <Route path="/rbac" element={<RbacPage />} />
    <Route path="/ai-agents" element={<AiAgentsPage />} />
    <Route
      path="/ai-agents/provider-accounts"
      element={<ProviderAccountListPage />}
    />
    <Route
      path="/ai-agents/provider-accounts/:accountId"
      element={<ProviderAccountDetailPage />}
    />
    <Route path="/ai-agents/:agentId" element={<AiAgentDetailPage />} />
    <Route
      path="/ai-agents/provider-accounts"
      element={<ProviderAccountListPage />}
    />
    <Route
      path="/ai-agents/provider-accounts/:accountId"
      element={<ProviderAccountDetailPage />}
    />
    <Route path="/billing" element={<Navigate to="/billing/overview" />} />
    <Route path="/billing/overview" element={<BillingOverviewPage />} />
    <Route path="/billing/sp-usage" element={<BillingSpUsagePage />} />
    <Route path="/billing/invoice" element={<BillingInvoicePage />} />
    <Route path="/billing/invoices" element={<InvoiceListPage />} />
    <Route path="/billing/invoices/create" element={<InvoiceCreatePage />} />
    <Route path="/billing/invoices/:id/edit" element={<InvoiceEditPage />} />
    <Route
      path="/billing/term-of-payment"
      element={<TermOfPaymentListPage />}
    />
    <Route
      path="/billing/term-of-payment/create"
      element={<TermOfPaymentCreatePage />}
    />
    <Route
      path="/billing/term-of-payment/:id/edit"
      element={<TermOfPaymentEditPage />}
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <AutoTriggerOAuthDialog />
    <SignalsDisplay />
    <AppRouter>
      <VisitListener />
      <SignedInFirebaseNotificationListener />
      <ZitadelAccessInterceptor>
        <AppLayout>{routes}</AppLayout>
      </ZitadelAccessInterceptor>
    </AppRouter>
  </>,
);
